/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from "react";
import "~/less/web-internal.less";
import { cls } from "cls";
import { logException } from "@wss/error-tracking";
import { toError } from "@wss/error-tracking/utils";
import { ThrowableStatus } from "@wss/error-tracking/throwable";
import connectUserAndChat from "~/common/Store/connectUserAndChat";
import { User } from "~/common/Store/models/User";
import { InternalNotesRecentUpdateResult } from "../ProductDetailPageResult";
import useKeymap from "~/shared/hooks/useKeymap";
import useLoggedInAs from "~/common/hooks/useLoggedInAs";
import throwableHttpRetry from "~/common/utils/throwableHttpRetry";
interface Props {
  showInternalNotesModal: boolean;
  toggleInternalNotesModal: () => void;
  user: User;
  itemNumber: string;
}
const InternalNotesButton = connectUserAndChat(({
  showInternalNotesModal,
  toggleInternalNotesModal,
  user,
  itemNumber
}: Props) => {
  const showLoggedInAsBanner = useLoggedInAs(user);
  const [internalNotesRecentUpdate, setInternalNotesRecentUpdate] = useState<InternalNotesRecentUpdateResult>({
    dateModified: "",
    userName: ""
  });
  useKeymap({
    "alt+`": toggleInternalNotesModal
  });
  const isInternal = user.analyticsData?.is_on_vpn?.toLocaleUpperCase() === "YES";
  const loggedInAsBannerStyles = cls("bottom-[5.5rem]", "sm:bottom-[4.5rem]", "lg:bottom-2.5");
  const dateModifiedFormatted = internalNotesRecentUpdate.dateModified?.length ? new Date(internalNotesRecentUpdate.dateModified).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric"
  }) : "";
  useEffect(() => {
    async function logError(e: unknown) {
      const error = new Error("Error in getRecentInternalNotesUpdate", {
        cause: toError(e)
      });
      await logException(error);
      setInternalNotesRecentUpdate({
        dateModified: "",
        userName: ""
      });
      return error;
    }
    async function getRecentInternalNotesUpdate(): Promise<ThrowableStatus<InternalNotesRecentUpdateResult>> {
      const response = await fetch(`/api/productdetails/internalnotesupdate?itemNumber=${encodeURIComponent(itemNumber)}`);
      if (!response.ok) {
        return {
          isError: true,
          status: response.status,
          error: toError(response.body)
        };
      }
      return {
        isError: false,
        value: await response.json<InternalNotesRecentUpdateResult>()
      };
    }
    async function updateRecentInternalNotes(): Promise<ThrowableStatus> {
      try {
        const result = await throwableHttpRetry(getRecentInternalNotesUpdate, 2, 200, [408, 500, 502, 503, 504]);
        if (result.isError) {
          const {
            error,
            status
          } = result;

          // skip logging for expected unactionable HTTP status codes, log the rest.
          if (!status || ![401, 408, 502, 503, 504].includes(status)) {
            return {
              isError: true,
              error,
              status
            };
          }
          return {
            isError: true,
            error: await logError(error),
            status
          };
        }
        setInternalNotesRecentUpdate(result.value);
      } catch (e) {
        return {
          isError: true,
          error: await logError(e),
          status: null
        };
      }
      return {
        isError: false
      };
    }
    if (isInternal) {
      updateRecentInternalNotes();
    }
  }, [isInternal, itemNumber]);
  return internalNotesRecentUpdate.dateModified?.length > 0 || internalNotesRecentUpdate.userName?.length > 0 ? <a type="button" onClick={toggleInternalNotesModal} id="web-internal-trigger" className={cls("webinfo-trigger", "webinfo-trigger--attention", "webinfo-trigger--expanded", showInternalNotesModal && "active", showLoggedInAsBanner && loggedInAsBannerStyles)} data-testid="show-internal-notes-button">
        <i className="icon-attention" />
        <span className="webinfo_heading">
          Internal Notes
          {(internalNotesRecentUpdate.userName?.length > 0 || internalNotesRecentUpdate.dateModified?.length > 0) && <span className="webinfo__meta">
              {dateModifiedFormatted?.length > 0 && <strong data-testid="internal-notes-date-updated">{`${dateModifiedFormatted} `}</strong>}
              {internalNotesRecentUpdate.userName?.length > 0 && <>
                  {"by "}
                  <strong data-testid="internal-notes-user-updated">
                    {internalNotesRecentUpdate.userName}
                  </strong>
                </>}
            </span>}
        </span>
      </a> : null;
});
export default InternalNotesButton;